import { useQuery } from "@tanstack/react-query";
import { Session } from "@supabase/supabase-js";
import { CalendarAlert, MarketAlert, NewsAlert } from "@/utils/api";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface AlertsError extends Error {
  status?: number;
}

// Calendar Alerts
export const fetchCalendarAlertsData = async (
  session?: Session | null
): Promise<CalendarAlert[]> => {
  console.log('useAlertsData: Starting fetch for calendar alerts', { 
    hasSession: !!session,
    userId: session?.user?.id
  });

  if (!session?.user?.id) {
    console.log('useAlertsData: No session available, skipping fetch');
    throw new Error('No session available');
  }
  
  try {
    console.log('useAlertsData: Calling alerts function for calendar alerts');

    const { data, error } = await supabase.functions.invoke<CalendarAlert[]>('Screener-and-Alerts', {
      body: {
        function: 'alerts',
        alertType: 'calendar'
      }
    });

    if (error) {
      console.error('useAlertsData: Edge function error:', error);
      
      // Check if this is a FunctionsHttpError (edge function returned non-2xx status)
      if (error.message?.includes('Edge Function returned a non-2xx status code') || 
          error.name === 'FunctionsHttpError') {
        console.error('useAlertsData: Edge function returned error status code - returning empty array');
        
        toast({
          title: "Calendar alerts temporarily unavailable",
          description: "Our servers are experiencing high demand. Please try again in a few minutes.",
          variant: "default",
        });
        
        // Return empty array as fallback
        return [];
      }
      
      throw error;
    }

    if (!data) {
      console.error('useAlertsData: No data returned from edge function');
      throw new Error('No data returned from alerts API');
    }

    console.log('useAlertsData: Calendar alerts fetched successfully', {
      count: data.length
    });

    return data;
  } catch (error) {
    console.error('useAlertsData: Error in fetchCalendarAlertsData:', error);
    
    const castedError = error as AlertsError;
    
    // No more mock data fallbacks - using only edge functions
    console.log('useAlertsData: Edge function required for alerts data');
    
    toast({
      title: "Error loading calendar alerts",
      description: castedError.message || "Failed to load calendar alerts. Please try again.",
      variant: "destructive",
    });
    
    throw castedError;
  }
};

// Market Alerts
export const fetchMarketAlertsData = async (
  session?: Session | null
): Promise<MarketAlert[]> => {
  console.log('useAlertsData: Starting fetch for market alerts', { 
    hasSession: !!session,
    userId: session?.user?.id
  });

  if (!session?.user?.id) {
    console.log('useAlertsData: No session available, skipping fetch');
    throw new Error('No session available');
  }
  
  try {
    console.log('useAlertsData: Calling alerts function for market alerts');

    const { data, error } = await supabase.functions.invoke<MarketAlert[]>('Screener-and-Alerts', {
      body: {
        function: 'alerts',
        alertType: 'market'
      }
    });

    if (error) {
      console.error('useAlertsData: Edge function error:', error);
      
      // Check if this is a FunctionsHttpError (edge function returned non-2xx status)
      if (error.message?.includes('Edge Function returned a non-2xx status code') || 
          error.name === 'FunctionsHttpError') {
        console.error('useAlertsData: Edge function returned error status code - returning empty array');
        
        toast({
          title: "Market alerts temporarily unavailable",
          description: "Our servers are experiencing high demand. Please try again in a few minutes.",
          variant: "default",
        });
        
        // Return empty array as fallback
        return [];
      }
      
      throw error;
    }

    if (!data) {
      console.error('useAlertsData: No data returned from edge function');
      throw new Error('No data returned from alerts API');
    }

    console.log('useAlertsData: Market alerts fetched successfully', {
      count: data.length
    });

    return data;
  } catch (error) {
    console.error('useAlertsData: Error in fetchMarketAlertsData:', error);
    
    const castedError = error as AlertsError;
    
    // No more mock data fallbacks - using only edge functions
    console.log('useAlertsData: Edge function required for alerts data');
    
    toast({
      title: "Error loading market alerts",
      description: castedError.message || "Failed to load market alerts. Please try again.",
      variant: "destructive",
    });
    
    throw castedError;
  }
};

// News Alerts
export const fetchNewsAlertsData = async (
  session?: Session | null
): Promise<NewsAlert[]> => {
  console.log('useAlertsData: Starting fetch for news alerts', { 
    hasSession: !!session,
    userId: session?.user?.id
  });

  if (!session?.user?.id) {
    console.log('useAlertsData: No session available, skipping fetch');
    throw new Error('No session available');
  }
  
  try {
    console.log('useAlertsData: Calling alerts function for news alerts');

    const { data, error } = await supabase.functions.invoke<NewsAlert[]>('Screener-and-Alerts', {
      body: {
        function: 'alerts',
        alertType: 'news'
      }
    });

    if (error) {
      console.error('useAlertsData: Edge function error:', error);
      
      // Check if this is a FunctionsHttpError (edge function returned non-2xx status)
      if (error.message?.includes('Edge Function returned a non-2xx status code') || 
          error.name === 'FunctionsHttpError') {
        console.error('useAlertsData: Edge function returned error status code - returning empty array');
        
        toast({
          title: "News alerts temporarily unavailable", 
          description: "Our servers are experiencing high demand. Please try again in a few minutes.",
          variant: "default",
        });
        
        // Return empty array as fallback
        return [];
      }
      
      throw error;
    }

    if (!data) {
      console.error('useAlertsData: No data returned from edge function');
      throw new Error('No data returned from alerts API');
    }

    console.log('useAlertsData: News alerts fetched successfully', {
      count: data.length
    });

    return data;
  } catch (error) {
    console.error('useAlertsData: Error in fetchNewsAlertsData:', error);
    
    const castedError = error as AlertsError;
    
    // No more mock data fallbacks - using only edge functions
    console.log('useAlertsData: Edge function required for alerts data');
    
    toast({
      title: "Error loading news alerts",
      description: castedError.message || "Failed to load news alerts. Please try again.",
      variant: "destructive",
    });
    
    throw castedError;
  }
};

// "Mark as Sent" functionality has been removed
// This feature is no longer supported in the current implementation

export const useCalendarAlertsData = (
  session?: Session | null,
  options?: {
    staleTime?: number;
    gcTime?: number;
    retry?: boolean | number;
    enabled?: boolean;
  }
) => {
  return useQuery({
    queryKey: ['calendar-alerts', session?.user?.id],
    queryFn: () => fetchCalendarAlertsData(session),
    enabled: options?.enabled ?? !!session?.user?.id,
    staleTime: options?.staleTime ?? 1000 * 60 * 5, // 5 minutes
    gcTime: options?.gcTime ?? 1000 * 60 * 10, // 10 minutes
    retry: options?.retry ?? 3,
  });
};

export const useMarketAlertsData = (
  session?: Session | null,
  options?: {
    staleTime?: number;
    gcTime?: number;
    retry?: boolean | number;
    enabled?: boolean;
  }
) => {
  return useQuery({
    queryKey: ['market-alerts', session?.user?.id],
    queryFn: () => fetchMarketAlertsData(session),
    enabled: options?.enabled ?? !!session?.user?.id,
    staleTime: options?.staleTime ?? 1000 * 60 * 5, // 5 minutes
    gcTime: options?.gcTime ?? 1000 * 60 * 10, // 10 minutes
    retry: options?.retry ?? 3,
  });
};

export const useNewsAlertsData = (
  session?: Session | null,
  options?: {
    staleTime?: number;
    gcTime?: number;
    retry?: boolean | number;
    enabled?: boolean;
  }
) => {
  return useQuery({
    queryKey: ['news-alerts', session?.user?.id],
    queryFn: () => fetchNewsAlertsData(session),
    enabled: options?.enabled ?? !!session?.user?.id,
    staleTime: options?.staleTime ?? 1000 * 60 * 5, // 5 minutes
    gcTime: options?.gcTime ?? 1000 * 60 * 10, // 10 minutes
    retry: options?.retry ?? 3,
  });
};